import { useState, useEffect, useContext, useRef, useMemo } from "react";

import NewReflection from "../../components/modals/new-reflection";
import NewForecast from "../../components/modals/new-forecast";
import SingleForecast from "./single-forecasts";
import SingleReflection from "./single-reflections";
import EconomicCalendar from "../economic-calendar";
import AuthContext from "../../lib/auth-context";
import ThemeContext from "../../lib/theme-context";
import { DataFetcher } from "../../utils/dataFetcher";
import moment from "moment";
import { getJSON } from "../../utils/safeLocalStorage";
import { calculateDailyStats, calculateWeeklyStats } from "../../utils/calendarStatsCalculator";
import StatsCalendarDayCell from "../../components/calendar/StatsCalendarDayCell";
import UnarchivedTestsDropDown from "../../components/select/unarchivedTestsDropDown";

import CalendarGrid from "../../components/calendar/CalendarGrid";
import CalendarListView from "../../components/calendar/CalendarListView";
import DailyView from "../../components/calendar/DailyView";

import { animateScroll } from "react-scroll";

import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BarChartIcon from "@mui/icons-material/BarChart";
import ArticleIcon from "@mui/icons-material/Article";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FilterListIcon from "@mui/icons-material/FilterList";
import Journal from "../../components/modals/journal";
import useWindowDimensions from "../../utils/width";

const Calender = () => {
  const { width } = useWindowDimensions();
  const auth = useContext(AuthContext);
  const theme = useContext(ThemeContext);
  const effectRan = useRef(false);
  const [openForecast, setOpenForecast] = useState(false);
  const [openReflection, setOpenReflection] = useState(false);
  const [singleForecastView, setSingleForecastView] = useState();
  const [singleReflectionView, setSingleReflectionView] = useState();
  const [singleJournalView, setSingleJournalView] = useState();
  const [eventMoveBool, setEventMoveBool] = useState(false);
  const [openJournal, setOpenJournal] = useState(false);
  const [calendarView, setCalendarView] = useState(
    localStorage.getItem("calendar-display-view") || "month",
  );
  const [statsView, setStatsView] = useState(
    localStorage.getItem("calendar-stats-view") === "true" || false,
  );
  const [newsView, setNewsView] = useState(
    localStorage.getItem("calendar-news-view") === "true" || false,
  );
  const [currentMonth, setCurrentMonth] = useState(moment());
  const selectedAccountId = localStorage.getItem("selected-account-id") || "All";
  const [selectedBacktestId, setSelectedBacktestId] = useState(
    localStorage.getItem("selected-backtest-id") || "All"
  );
  const [showGross, setShowGross] = useState(() => localStorage.getItem("show-gross") === "true");

  // Listen for gross-net-changed events from dashboard
  useEffect(() => {
    const handleGrossNetChanged = (event) => {
      if (event.detail && event.detail.showGross !== undefined) {
        setShowGross(event.detail.showGross);
      }
    };

    window.addEventListener("gross-net-changed", handleGrossNetChanged);
    
    return () => {
      window.removeEventListener("gross-net-changed", handleGrossNetChanged);
    };
  }, []);

  // Calculate monthly stats with useMemo to prevent unnecessary recalculations
  const monthlyStats = useMemo(() => {
    if (!statsView) return null;
    
    const startOfMonth = moment(currentMonth).startOf("month");
    const endOfMonth = moment(currentMonth).endOf("month");
    
    const baseTrades = auth.backtestingMode
      ? auth.trades.filter((t) => t.id?.includes("backtest-"))
      : auth.trades.filter((t) => t.id?.includes("live-"));
    
    // Filter by backtest or account depending on mode
    let filteredTrades;
    if (auth.backtestingMode) {
      if (selectedBacktestId === "All") {
        filteredTrades = baseTrades;
      } else if (selectedBacktestId === "Missed Trades") {
        filteredTrades = baseTrades.filter(trade => trade.missedTrade);
      } else {
        filteredTrades = baseTrades.filter(trade => trade.testId === selectedBacktestId);
      }
    } else {
      const getFilteredTradesByAccount = (trades, accountId) => {
        if (accountId === "All") {
          return trades;
        } else if (accountId === "All Personal") {
          return trades.filter(trade => trade.equityType === "Personal Capital");
        } else if (accountId === "All Investor") {
          return trades.filter(trade => trade.equityType === "Investor Capital");
        }
        return trades.filter(trade => trade.accountId === accountId);
      };
      filteredTrades = getFilteredTradesByAccount(baseTrades, selectedAccountId);
    }
    
    const monthTrades = (filteredTrades || []).filter(t => {
      if (!t.entryDate) return false;
      const tradeDate = moment(t.entryDate, 'DD/MM/YY');
      return tradeDate.isValid() && 
             tradeDate.isSameOrAfter(startOfMonth, 'day') && 
             tradeDate.isSameOrBefore(endOfMonth, 'day');
    });
    
    const totalTrades = monthTrades.length;
    const tradingDays = monthTrades.map(t => moment(t.entryDate, 'DD/MM/YY').format('YYYY-MM-DD'));
    const uniqueTradingDays = new Set(tradingDays).size;

    const getTradePnL = (trade) => {
      const capital = trade.equityType || trade.capitalType;
      let grossPnL = 0;
      let fee = 0;
      let swap = 0;
      
      if (capital === "Investor Capital") {
        grossPnL = parseFloat(trade.profitLossDollarInvestor || 0);
        fee = parseFloat(trade.feeIC || 0);
        swap = parseFloat(trade.swapIC || 0);
      } else {
        grossPnL = parseFloat(trade.profitLossDollarPersonal || 0);
        fee = parseFloat(trade.feePC || 0);
        swap = parseFloat(trade.swapPC || 0);
      }
      
      const netPnL = grossPnL + fee + swap;
      return showGross ? grossPnL : netPnL;
    };
    
    const monthStats = calculateWeeklyStats(filteredTrades, currentMonth.clone().startOf('month'), currentMonth.clone().endOf('month'), showGross);
    
    const winningTrades = monthTrades.filter(t => getTradePnL(t) > 0);
    const losingTrades = monthTrades.filter(t => getTradePnL(t) < 0);
    const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0;
    
    const totalWins = winningTrades.reduce((sum, t) => sum + getTradePnL(t), 0);
    const totalLosses = Math.abs(losingTrades.reduce((sum, t) => sum + getTradePnL(t), 0));
    const monthlyPnL = monthStats.grossPnL;
    const percentageReturn = monthStats.percentReturn;
    
    const profitFactor = totalLosses > 0 ? totalWins / totalLosses : totalWins > 0 ? 999 : 0;
    const avgWin = winningTrades.length > 0 ? totalWins / winningTrades.length : 0;
    const avgLoss = losingTrades.length > 0 ? totalLosses / losingTrades.length : 0;
    const avgWinLoss = avgLoss > 0 ? avgWin / avgLoss : avgWin > 0 ? 999 : 0;

    return {
      monthlyPnL,
      percentageReturn,
      profitFactor,
      avgWinLoss,
      winRate,
      totalTrades,
      uniqueTradingDays
    };
  }, [statsView, currentMonth, selectedAccountId, selectedBacktestId, showGross, auth.backtestingMode, auth.trades]);

  // Responsive button styles based on screen width
  const getButtonStyles = () => ({
    padding: width >= 900 ? "6px 12px" : width >= 790 ? "6px 12px" : width >= 500 ? "5px 10px" : "4px 8px",
    fontSize: width >= 790 ? "12px" : width >= 500 ? "12px" : "11px",
    borderRadius: width >= 790 ? "18px" : "16px",
    fontWeight: "600",
    letterSpacing: width >= 500 ? "0.3px" : "0.2px",
    transition: "all 0.2s ease",
    cursor: "pointer",
  });

  // const pickColorForPair = (pair) => {
  //   if (pair.startsWith("AUD")) {
  //     return "#287271";
  //   } else if (pair.startsWith("GBP")) {
  //     return "#6e78ff";
  //   } else if (pair.startsWith("NZD")) {
  //     return "#ff87ab";
  //   } else if (pair.startsWith("CAD")) {
  //     return "#8ab17d";
  //   } else if (pair.startsWith("CHF")) {
  //     return "#e9c46a";
  //   } else if (pair.startsWith("EUR")) {
  //     return "#f4a261";
  //   } else if (pair.startsWith("USD")) {
  //     return "#e76f51";
  //   }
  // };

  const checkIfForecastIsNotAssignedToABacktest = () => {
    const forecast_Ids = auth.forecastsBacktesting.map((f) => {
      return f._id;
    });

    let trades = auth.trades.filter((trades) =>
      trades.id?.includes("backtest-"),
    );

    trades = trades.map((t) => t.foreCastId);

    const forecastIdsThatDontHaveTradesAssigned = forecast_Ids.filter(
      (b) => !trades.some((a) => a === b),
    );

    // Also exclude forecasts linked to journals from archived backtests
    const activeBacktestIds = auth.backtests
      .filter((tests) => tests.archived === "false")
      .map((t) => t.testId);

    const archivedBacktestTrades = auth.trades.filter(
      (trade) =>
        trade.id?.includes("backtest-") &&
        trade.testId &&
        !activeBacktestIds.some((id) => trade.testId.includes(id)),
    );

    const archivedJournalForecastIds =
      auth.journalsBacktesting
        ?.filter((j) => j.foreCastId && j.trade_id)
        ?.filter((j) =>
          archivedBacktestTrades.some((t) => t._id === j.trade_id),
        )
        ?.map((j) => j.foreCastId) || [];

    const trulyUnassigned = forecastIdsThatDontHaveTradesAssigned.filter(
      (fId) => !archivedJournalForecastIds.includes(fId),
    );

    const matchingForecastIds = auth.forecastsBacktesting.filter((l) => {
      return trulyUnassigned.some((term) => l._id.includes(term));
    });

    return matchingForecastIds;
  };

  const checkfForecastsToHide = () => {
    const array = auth.trades.filter((trades) =>
      trades.id?.includes("backtest-"),
    );

    const backtests = auth.backtests
      .filter((tests) => tests.archived === "false")
      .map((t) => t.testId);

    const matchingForecastIds = array.filter((l) => {
      return backtests.some((term) => l.testId.includes(term));
    });

    const foundIds = matchingForecastIds?.map((i) => i.foreCastId).filter(Boolean);

    const matching = auth.forecastsBacktesting.filter((l) => {
      return foundIds.some((term) => l._id.includes(term));
    });

    // concat matching with no linked trade forecast here

    const joinArray =
      checkIfForecastIsNotAssignedToABacktest().concat(matching);

    return joinArray.map((f) => {
      return {
        title: `${f.pair} Forecast`,
        date: moment(f.date).format("YYYY-MM-DD"),
        color: "#6b7280",
        publicId: f._id,
        forecastType: f.forecastType,
        direction: f.direction,
        category: "forecast",
      };
    });
  };


  const allForecastsBacktestingModeChecked = checkfForecastsToHide();

  const allForecastsModeChecked = auth.forecasts.map((f) => {
    return {
      title: `${f.pair} Forecast`,
      date: moment(f.date).format("YYYY-MM-DD"),
      color: "#6b7280",
      publicId: f._id,
      forecastType: f.forecastType,
      direction: f.direction,
      category: "forecast",
    };
  });

  const allTradesModeChecked = () => {
    const array = auth.trades
      .filter((trades) => !trades.missedTrade)
      .filter((trades) =>
        trades.id?.includes(auth.backtestingMode ? "backtest-" : "live-"),
      );
    
    let matchingTrades;
    if (auth.backtestingMode) {
      // Filter by selected backtest
      if (selectedBacktestId === "All") {
        const backtests = auth.backtests
          .filter((tests) => tests.archived === "false")
          .map((t) => t.testId);
        matchingTrades = array.filter((l) => {
          return backtests.some((term) => l.testId.includes(term));
        });
      } else if (selectedBacktestId === "Missed Trades") {
        matchingTrades = array.filter(trade => trade.missedTrade);
      } else {
        const isActive = auth.backtests?.some(
          (test) => test.testId === selectedBacktestId && test.archived === "false"
        );
        if (isActive) {
          matchingTrades = array.filter(trade => trade.testId === selectedBacktestId);
        } else {
          matchingTrades = [];
        }
      }
    } else {
      matchingTrades = array;
    }

    if (auth.backtestingMode) {
      return matchingTrades.map((f) => {
        return {
          title: f.pair,
          date: moment(f.created).format("YYYY-MM-DD"),
          color: f.direction === "Long" ? "#0079cf" : "#db0671",
          publicId: f._id,
          category: "trade",
          direction: f.direction,
        };
      });
    } else {
      return array.map((f) => {
        return {
          title: f.pair,
          date: moment(f.created).format("YYYY-MM-DD"),
          color: f.direction === "Long" ? "#0079cf" : "#db0671",
          publicId: f._id,
          category: "trade",
          direction: f.direction,
        };
      });
    }
  };

  const missedTradesModeChecked = () => {
    let filteredTrades = auth?.trades.filter((trades) => trades.missedTrade === true);

    // Always filter out missed trades from archived backtests
    const activeBacktestIds = auth.backtests
      .filter((tests) => tests.archived === "false")
      .map((t) => t.testId);
    filteredTrades = filteredTrades.filter((trade) => {
      // Missed trades with testId "Missed Trades" are not tied to a specific backtest
      if (trade.testId === "Missed Trades") return true;
      return activeBacktestIds.some((id) => trade.testId?.includes(id));
    });

    // Filter by selected backtest if in backtesting mode
    if (auth.backtestingMode && selectedBacktestId !== "All" && selectedBacktestId !== "Missed Trades") {
      const isActive = auth.backtests?.some(
        (test) => test.testId === selectedBacktestId && test.archived === "false"
      );
      if (isActive) {
        filteredTrades = filteredTrades.filter(trade => trade.testId === selectedBacktestId);
      } else {
        filteredTrades = [];
      }
    }

    return filteredTrades?.map((f) => {
      return {
        title: f.pair,
        date: moment(f.created).format("YYYY-MM-DD"),
        color: f.direction === "Long" ? "#0079cf" : "#db0671",
        publicId: f._id,
        category: "trade",
        direction: f.direction,
        isMissedTrade: true,
      };
    });
  };

  const allReflectionsModeChecked = auth.reflections
    .filter((f) => {
      const isBacktestReflection = f.typeOfReview === "Backtest";
      const hasValidTestId = !!(
        f.testId &&
        f.testId !== "" &&
        f.testId !== "undefined"
      );

      // For backtest reflections, check if the associated backtest is archived
      if (isBacktestReflection || hasValidTestId) {
        const associatedBacktest = auth.backtests?.find(
          (test) => test.testId === f.testId || test.user_id === f.user_id,
        );
        if (associatedBacktest?.archived !== "false") {
          return false;
        }
      }

      const shouldShow = auth.backtestingMode
        ? isBacktestReflection || hasValidTestId
        : !isBacktestReflection && !hasValidTestId;
      return shouldShow;
    })
    .map((f) => {
      // Handle backtest reflections (by typeOfReview or valid testId)
      if (
        f.typeOfReview === "Backtest" ||
        (f.testId && f.testId !== "" && f.testId !== "undefined")
      ) {
        const backtest = auth.backtests?.find(
          (test) => test.user_id === f.user_id,
        );
        const testId = backtest?.testId || "Backtest";
        const maxLength = 25;
        const truncatedTestId = testId.length > maxLength 
          ? testId.substring(0, maxLength) + "..." 
          : testId;
        return {
          title: truncatedTestId,
          date: moment(f.date).format("YYYY-MM-DD"),
          color: "#14b8a6", // Teal/cyan color for backtest reflections
          publicId: f._id,
          category: "reflection",
        };
      }

      // Handle time-based reflections (only in live mode)
      return {
        title: `${f.typeOfReview} Reflection`,
        date: moment(f.date).format("YYYY-MM-DD"),
        color:
          f.typeOfReview === "Weekly"
            ? "#8b5cf6"
            : f.typeOfReview === "Monthly"
              ? "#00A36C"
              : f.typeOfReview === "Annual"
                ? "#9F2B68"
                : f.typeOfReview?.includes("Q")
                  ? "#ED9E0E"
                  : null,
        publicId: f._id,
        category: "reflection",
      };
    });

  const forecasts =
    auth.backtestingMode && auth.defaultValue === "All Forecast"
      ? allForecastsBacktestingModeChecked
      : !auth.backtestingMode && auth.defaultValue === "All Forecast"
        ? allForecastsModeChecked
        : auth.defaultValue === "All" && !auth.backtestingMode
          ? allForecastsModeChecked
          : auth.defaultValue === "All" && auth.backtestingMode
            ? allForecastsBacktestingModeChecked
            : [];

  const holidayEvents = (auth.holidays || []).map((holiday) => ({
    title: holiday.name,
    date: holiday.date,
    color: theme[0] === "dark" ? "#ffffff" : "#000000",
    publicId: `holiday-${holiday.date}-${holiday.currency}`,
    category: "holiday",
    currency: holiday.currency,
    country: holiday.country,
  }));

  const handleOpenNewReflection = () => {
    setOpenReflection(true);
  };
  const handleOpenNewForecast = () => setOpenForecast(true);
  const handleCloseNewForecast = () => setOpenForecast(false);
  const handleCloseNewReflection = () => setOpenReflection(false);

  const handleCloseJournal = () => {
    localStorage.removeItem("journal-portal");
    localStorage.removeItem("trade-portal");
    setOpenJournal(false);
    setSingleReflectionView(true);
  };

  const scrollToBottom = () => {
    const options = {
      duration: 500,
      smooth: true,
    };

    animateScroll.scrollToBottom(options);
  };
  const handleScroll = () => {
    if (moment().format("D") >= 16) {
      scrollToBottom();
    }
  };

  useEffect(() => {
    if (effectRan.current === false) {
      // handleScroll();
      if (localStorage.getItem("forecast-to-view")) {
        setSingleForecastView(true);
      }
      if (localStorage.getItem("reflection-to-view")) {
        setSingleReflectionView(true);
      }
      if (!auth.backtestingMode && auth.defaultValue === "Missed Trades") {
        auth.setDefaultValue("All");
      }

      return () => {
        effectRan.current = true;
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate selected backtest is still active; reset to "All" if archived
  useEffect(() => {
    if (auth.backtestingMode && selectedBacktestId && selectedBacktestId !== "All" && selectedBacktestId !== "Missed Trades") {
      const isActive = auth.backtests?.some(
        (test) => test.testId === selectedBacktestId && test.archived === "false"
      );
      if (!isActive) {
        setSelectedBacktestId("All");
        localStorage.setItem("selected-backtest-id", "All");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.backtests, auth.backtestingMode, selectedBacktestId]);

  const eventMove = async (event, newDate) => {
    const id = event.publicId;
    const category = event.category;
    
    if (category === "trade") {
      auth.throwMessage("info", "Not Allowed");
      return;
    } else if (category === "reflection") {
      const result = await DataFetcher("edit-reflection", {
        _id: id,
        date: moment(newDate),
      });
      setEventMoveBool(true);
      if (result) {
        const filteredArray = auth.reflections.filter((x) => x._id !== id);
        filteredArray.push(result);
        auth.setReflections(filteredArray);
        setTimeout(() => {
          setEventMoveBool(false);
        }, 600);
      }
    } else if (category === "forecast") {
      const result = await DataFetcher("edit-forecast", {
        _id: id,
        date: moment(newDate),
      });
      if (result) {
        if (auth.backtestingMode) {
          const filteredArray = auth.forecastsBacktesting.filter(
            (x) => x._id !== id,
          );
          filteredArray.push(result);
          auth.setForecastsBacktesting(filteredArray);
        } else {
          const filteredArray = auth.forecasts.filter((x) => x._id !== id);
          filteredArray.push(result);
          auth.setForecasts(filteredArray);
        }
      }
    }
  };
  const speed = () => {
    if (auth.backtestingMode) {
      return [
        {
          icon: <QueryStatsIcon onClick={handleOpenNewForecast} />,
          name: "Forecast",
        },
        {
          icon: <AutoStoriesIcon onClick={handleOpenNewReflection} />,
          name: "Reflect",
        },
      ];
    } else {
      return [
        {
          icon: <QueryStatsIcon onClick={handleOpenNewForecast} />,
          name: "Forecast",
        },
        {
          icon: <AutoStoriesIcon onClick={handleOpenNewReflection} />,
          name: "Reflect",
        },
      ];
    }
  };

  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth);
    localStorage.setItem("view-date", newMonth.toDate());
  };

  const handleViewToggle = (view) => {
    setCalendarView(view);
    localStorage.setItem("calendar-display-view", view);

    if (statsView) {
      setStatsView(false);
      localStorage.setItem("calendar-stats-view", "false");
    }
    if (newsView) {
      setNewsView(false);
      localStorage.setItem("calendar-news-view", "false");
    }
  };

  const handleBacktestChange = (backtestId) => {
    setSelectedBacktestId(backtestId);
    localStorage.setItem("selected-backtest-id", backtestId);
  };

  const getFilteredTradesByAccount = (trades, accountId) => {
    if (!trades || trades.length === 0) return [];
    
    if (accountId === "All") {
      return trades;
    } else if (accountId === "All Personal") {
      return trades.filter(trade => trade.equityType === "Personal Capital");
    } else if (accountId === "All Investor") {
      return trades.filter(trade => trade.equityType === "Investor Capital");
    }
    
    const user = getJSON("user", {});
    const capitalAccounts = user.capitalAccounts || [];
    const selectedAccount = capitalAccounts.find((acc) => acc.id === accountId);

    if (!selectedAccount) {
      return trades;
    }

    return trades.filter((trade) => {
      if (trade.accountId) {
        return trade.accountId === accountId;
      }
      if (selectedAccount.type === "Personal") {
        return trade.equityType === "Personal Capital";
      } else if (selectedAccount.type === "Investor") {
        return trade.equityType === "Investor Capital";
      }
      return false;
    });
  };

  useEffect(() => {
    const date = localStorage.getItem("view-date");
    if (date) {
      setCurrentMonth(moment(new Date(date)));
    }
  }, [singleForecastView, singleReflectionView]);

  const handleEventClick = (event) => {
    if (!event) return;

    if (event.title.includes("Forecast")) {
      setSingleForecastView(true);
      localStorage.setItem("view-date", currentMonth.toDate());
      localStorage.setItem("forecast-to-view", event.publicId);
    } else if (
      event.title.includes("Weekly") ||
      event.title.includes("Monthly") ||
      event.title.includes("Annual") ||
      event.title.includes("Q") ||
      event.title.includes("Reflection") ||
      (event.publicId && auth.reflections.some(r => r._id === event.publicId))
    ) {
      if (!eventMoveBool) {
        localStorage.setItem("view-date", currentMonth.toDate());
        setSingleReflectionView(true);
        localStorage.setItem("reflection-to-view", event.publicId);
      }
    } else {
      localStorage.setItem("view-date", currentMonth.toDate());

      const tradeId = event.publicId;
      const clickedTrade = auth.trades.find((t) => t._id === tradeId);

      if (clickedTrade && clickedTrade.accountId) {
        localStorage.setItem("selected-account-id", clickedTrade.accountId);
      } else if (clickedTrade && clickedTrade.equityType) {
        const user = getJSON("user", {});
        const capitalAccounts = user.capitalAccounts || [];

        if (clickedTrade.equityType === "Personal Capital") {
          const personalAccount = capitalAccounts.find(
            (acc) => acc.type === "Personal" && acc.isActive
          );
          if (personalAccount) {
            localStorage.setItem("selected-account-id", personalAccount.id);
          }
        } else if (clickedTrade.equityType === "Investor Capital") {
          const investorAccount = capitalAccounts.find(
            (acc) => acc.type === "Investor" && acc.isActive
          );
          if (investorAccount) {
            localStorage.setItem("selected-account-id", investorAccount.id);
          }
        }
      }

      auth.setTab(1);
      localStorage.setItem("trade-to-view", tradeId);
      localStorage.removeItem("linked-trade-ids"); // Clear any previous linked trades filter
    }
  };

  return singleForecastView ? (
    <SingleForecast
      setSingleForecastView={setSingleForecastView}
      handleScroll={handleScroll}
      setSingleReflectionView={setSingleReflectionView}
    />
  ) : singleReflectionView ? (
    <SingleReflection
      setSingleReflectionView={setSingleReflectionView}
      handleScroll={handleScroll}
      setSingleJournalView={setSingleJournalView}
      setOpenJournal={setOpenJournal}
      setSingleForecastView={setSingleForecastView}
      handleCloseNewReflection={handleCloseNewReflection}
    />
  ) : singleJournalView ? (
    <Journal
      open={openJournal}
      onClose={handleCloseJournal}
      handleOpenNewReflection={handleOpenNewReflection}
      setSingleReflectionView={setSingleReflectionView}
      setSingleJournalView={setSingleJournalView}
      setOpenJournal={setOpenJournal}
      calculateFields={false}
    />
  ) : (
    <div
      style={{
        color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
        padding: width < 500 ? "0" : "0 1em",
        height: "auto",
        width: "100%",
        overflowY: statsView ? "auto" : "hidden",
        margin: "0 auto 0 auto",
        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
      }}
    >
      <div>
        <NewForecast open={openForecast} onClose={handleCloseNewForecast} />
        <NewReflection
          open={openReflection}
          onClose={handleCloseNewReflection}
          openReflection={openReflection}
          setSingleJournalView={setSingleJournalView}
          setOpenJournal={setOpenJournal}
          setSingleReflectionView={setSingleReflectionView}
          setSingleForecastView={setSingleForecastView}
        />

        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding:
              width >= 790
                ? "0.8em 0.8em 0.4em 0.8em"
                : "0.5em 16px 0.3em 16px",
          }}
        >
          {/* Single Row Navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: width < 790 ? "center" : "space-between",
              alignItems: width < 790 ? "center" : "flex-start",
              flexWrap: "wrap",
              gap: "12px",
              marginTop: width < 790 ? "0.5em" : "0",
            }}
          >
            {/* Left: Calendar/Stats Toggle + Account/Gross (mobile stats only) */}
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "center",
                width: width < 790 ? "100%" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#ffffff",
                  padding: "4px",
                  borderRadius: "12px",
                  border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
                }}
              >
              <div
                onClick={() => {
                  if (statsView) {
                    setStatsView(false);
                    localStorage.setItem("calendar-stats-view", "false");
                  }
                  if (newsView) {
                    setNewsView(false);
                    localStorage.setItem("calendar-news-view", "false");
                  }
                  if (width < 790) {
                    setCalendarView("month");
                    localStorage.setItem("calendar-display-view", "month");
                  }
                }}
                style={{
                  ...getButtonStyles(),
                  backgroundColor: !statsView && !newsView
                    ? theme[0] === "dark"
                      ? "rgba(59, 130, 246, 0.2)"
                      : "rgba(59, 130, 246, 0.15)"
                    : "transparent",
                  border: !statsView && !newsView ? `2px solid #3b82f6` : "2px solid transparent",
                  color: !statsView && !newsView
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: width >= 790 ? "16px" : "14px" }} />
                Calendar
              </div>
              {width < 790 && (
                <div
                  onClick={() => {
                    if (statsView) {
                      setStatsView(false);
                      localStorage.setItem("calendar-stats-view", "false");
                    }
                    if (newsView) {
                      setNewsView(false);
                      localStorage.setItem("calendar-news-view", "false");
                    }
                    handleViewToggle("daily");
                  }}
                  style={{
                    ...getButtonStyles(),
                    backgroundColor: calendarView === "daily" && !statsView && !newsView
                      ? theme[0] === "dark"
                        ? "rgba(34, 197, 94, 0.2)"
                        : "rgba(34, 197, 94, 0.15)"
                      : "transparent",
                    border: calendarView === "daily" && !statsView && !newsView
                      ? `2px solid #22c55e`
                      : "2px solid transparent",
                    color: calendarView === "daily" && !statsView && !newsView
                      ? "#22c55e"
                      : theme[0] === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <CalendarTodayIcon sx={{ fontSize: "14px" }} />
                  Daily
                </div>
              )}
              <div
                onClick={() => {
                  const newStatsView = !statsView;
                  setStatsView(newStatsView);
                  localStorage.setItem("calendar-stats-view", newStatsView.toString());
                  if (newStatsView && newsView) {
                    setNewsView(false);
                    localStorage.setItem("calendar-news-view", "false");
                  }
                }}
                style={{
                  ...getButtonStyles(),
                  backgroundColor: statsView
                    ? theme[0] === "dark"
                      ? "rgba(168, 85, 247, 0.2)"
                      : "rgba(168, 85, 247, 0.15)"
                    : "transparent",
                  border: statsView ? `2px solid #a855f7` : "2px solid transparent",
                  color: statsView
                    ? "#a855f7"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <BarChartIcon sx={{ fontSize: width >= 790 ? "16px" : "14px" }} />
                Stats
              </div>
              <div
                onClick={() => {
                  const newNewsView = !newsView;
                  setNewsView(newNewsView);
                  localStorage.setItem("calendar-news-view", newNewsView.toString());
                  if (newNewsView) {
                    setStatsView(false);
                    localStorage.setItem("calendar-stats-view", "false");
                  }
                }}
                style={{
                  ...getButtonStyles(),
                  backgroundColor: newsView
                    ? theme[0] === "dark"
                      ? "rgba(99, 102, 241, 0.2)"
                      : "rgba(99, 102, 241, 0.15)"
                    : "transparent",
                  border: newsView ? `2px solid #6366f1` : "2px solid transparent",
                  color: newsView
                    ? "#6366f1"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <ArticleIcon sx={{ fontSize: width >= 790 ? "16px" : "14px" }} />
                News
              </div>
              </div>

              {/* Mobile Stats View: Navigation buttons and date title */}
              {statsView && !newsView && width < 790 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    marginTop: "8px",
                    marginBottom: "8px",
                    width: "100%",
                  }}
                >
                  {/* Date title */}
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                      textAlign: "center",
                    }}
                  >
                    {currentMonth.format("MMMM YYYY")}
                  </div>
                  
                  {/* Navigation buttons */}
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <button
                      onClick={() => {
                        const newMonth = currentMonth.clone().subtract(1, "month");
                        setCurrentMonth(newMonth);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme[0] === "dark" ? "#3d3e43" : "#d1d5db";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme[0] === "dark" ? "#2d2e33" : "#e5e7eb";
                      }}
                      style={{
                        padding: "4px 8px",
                        backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e5e7eb",
                        color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                        border: "2px solid transparent",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight: "600",
                        fontSize: "11px",
                        transition: "all 0.2s ease",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => {
                        setCurrentMonth(moment());
                      }}
                      onMouseEnter={(e) => {
                        if (moment().isSame(currentMonth, "month")) {
                          e.currentTarget.style.transform = "scale(1.02)";
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(55, 136, 216, 0.3)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                      style={{
                        padding: "4px 8px",
                        backgroundColor: moment().isSame(currentMonth, "month")
                          ? theme[0] === "dark" ? "rgba(55, 136, 216, 0.2)" : "rgba(55, 136, 216, 0.15)"
                          : theme[0] === "dark" ? "#2d2e33" : "#e5e7eb",
                        color: moment().isSame(currentMonth, "month") ? "#3788d8" : theme[0] === "dark" ? "#e5e7eb" : "#374151",
                        border: "2px solid transparent",
                        borderColor: moment().isSame(currentMonth, "month") ? "#3788d8" : "transparent",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight: "600",
                        fontSize: "11px",
                        transition: "all 0.2s ease",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => {
                        const newMonth = currentMonth.clone().add(1, "month");
                        setCurrentMonth(newMonth);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme[0] === "dark" ? "#3d3e43" : "#d1d5db";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme[0] === "dark" ? "#2d2e33" : "#e5e7eb";
                      }}
                      style={{
                        padding: "4px 8px",
                        backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e5e7eb",
                        color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                        border: "2px solid transparent",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight: "600",
                        fontSize: "11px",
                        transition: "all 0.2s ease",
                        whiteSpace: "nowrap",
                        flex: 1,
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {/* Account selector (mobile stats view only) */}
              {statsView && !newsView && width < 790 && (
                <div style={{ display: "flex", gap: "8px", marginTop:"10px", marginBottom: "-10px" }}>

                  {auth.backtestingMode && (
                    <UnarchivedTestsDropDown
                      selectedTest={selectedBacktestId}
                      onTestSelect={handleBacktestChange}
                      showBacktestTrades={() => {}}
                      label="Select Backtest"
                      includeMissedTrades={true}
                      variant="compact"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Center: Filters (only in calendar view, not daily view) */}
            {!statsView && !newsView && calendarView !== "daily" && (
              <div
                style={{
                  display: "flex",
                  flexWrap: width < 790 ? "nowrap" : "wrap",
                  gap: width < 500 ? "4px" : "8px",
                  alignItems: "center",
                  justifyContent: "center",
                  width: width < 790 ? "100%" : "auto",
                  flex: width < 790 ? undefined : 1,
                  marginTop: width < 790 ? "8px" : "0",
                  overflowX: width < 790 ? "auto" : undefined,
                }}
              >
            {!statsView && !newsView && auth.backtestingMode && (
              <div
                onClick={() => {
                  auth.setDefaultValue("Missed Trades");
                  localStorage.setItem("calendar-view-mode", "Missed Trades");
                }}
                style={{
                  ...getButtonStyles(),
                  backgroundColor:
                    auth.defaultValue === "Missed Trades"
                      ? theme[0] === "dark"
                        ? "rgba(59, 130, 246, 0.2)"
                        : "rgba(59, 130, 246, 0.15)"
                      : theme[0] === "dark"
                        ? "rgba(156, 163, 175, 0.1)"
                        : "rgba(107, 114, 128, 0.05)",
                  border: `2px solid ${
                    auth.defaultValue === "Missed Trades"
                      ? "#3b82f6"
                      : theme[0] === "dark"
                        ? "#2d2e33"
                        : "#e5e7eb"
                  }`,
                  color:
                    auth.defaultValue === "Missed Trades"
                      ? "#3b82f6"
                      : theme[0] === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                }}
              >
                Missed
              </div>
            )}


            {!statsView && !newsView && (
              <div
                onClick={() => {
                  auth.setDefaultValue("Trades");
                  localStorage.setItem("calendar-view-mode", "Trades");
                }}
              style={{
                ...getButtonStyles(),
                backgroundColor:
                  auth.defaultValue === "Trades"
                    ? theme[0] === "dark"
                      ? "rgba(59, 130, 246, 0.2)"
                      : "rgba(59, 130, 246, 0.15)"
                    : theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.1)"
                      : "rgba(107, 114, 128, 0.05)",
                border: `2px solid ${
                  auth.defaultValue === "Trades"
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#2d2e33"
                      : "#e5e7eb"
                }`,
                color:
                  auth.defaultValue === "Trades"
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              >
                <TrendingUpIcon sx={{ fontSize: width >= 790 ? "16px" : "14px" }} />
                Trades
              </div>
            )}

            {!statsView && !newsView && (
              <div
                onClick={() => {
                  auth.setDefaultValue("Reflections");
                localStorage.setItem("calendar-view-mode", "Reflections");
              }}
              style={{
                ...getButtonStyles(),
                backgroundColor:
                  auth.defaultValue === "Reflections"
                    ? theme[0] === "dark"
                      ? "rgba(59, 130, 246, 0.2)"
                      : "rgba(59, 130, 246, 0.15)"
                    : theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.1)"
                      : "rgba(107, 114, 128, 0.05)",
                border: `2px solid ${
                  auth.defaultValue === "Reflections"
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#2d2e33"
                      : "#e5e7eb"
                }`,
                color:
                  auth.defaultValue === "Reflections"
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              >
                <AutoStoriesIcon sx={{ fontSize: width >= 790 ? "16px" : "14px" }} />
                Reflections
              </div>
            )}

            {!statsView && !newsView && (
              <div
                onClick={() => {
                  auth.setDefaultValue("All Forecasts");
                localStorage.setItem("calendar-view-mode", "All Forecasts");
              }}
              style={{
                ...getButtonStyles(),
                backgroundColor:
                  auth.defaultValue === "All Forecasts"
                    ? theme[0] === "dark"
                      ? "rgba(59, 130, 246, 0.2)"
                      : "rgba(59, 130, 246, 0.15)"
                    : theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.1)"
                      : "rgba(107, 114, 128, 0.05)",
                border: `2px solid ${
                  auth.defaultValue === "All Forecasts"
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#2d2e33"
                      : "#e5e7eb"
                }`,
                color:
                  auth.defaultValue === "All Forecasts"
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
              >
                <QueryStatsIcon sx={{ fontSize: width >= 790 ? "16px" : "14px" }} />
                Forecasts
              </div>
            )}

            <div
              onClick={() => {
                auth.setDefaultValue("All");
                localStorage.setItem("calendar-view-mode", "All");
              }}
              style={{
                ...getButtonStyles(),
                backgroundColor:
                  auth.defaultValue === "All"
                    ? theme[0] === "dark"
                      ? "rgba(59, 130, 246, 0.2)"
                      : "rgba(59, 130, 246, 0.15)"
                    : theme[0] === "dark"
                      ? "rgba(156, 163, 175, 0.1)"
                      : "rgba(107, 114, 128, 0.05)",
                border: `2px solid ${
                  auth.defaultValue === "All"
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#2d2e33"
                      : "#e5e7eb"
                }`,
                color:
                  auth.defaultValue === "All"
                    ? "#3b82f6"
                    : theme[0] === "dark"
                      ? "#9ca3af"
                      : "#6b7280",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <FilterListIcon sx={{ fontSize: width >= 790 ? "16px" : "14px" }} />
              All
            </div>
              </div>
            )}

            {/* Right: Monthly Stats + View Mode Toggles */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: width >= 790 ? "flex-end" : "flex-start",
                width: width >= 790 ? "auto" : "100%",
              }}
            >
              {/* Monthly Stats (only in stats view) */}
              {statsView && monthlyStats && (() => {
                const StatCard = ({ label, value, icon }) => (
                  <div
                    style={{
                      backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#f3f4f6",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                      minWidth: "80px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: theme[0] === "dark" ? "#6b7280" : "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {icon && <span>{icon}</span>}
                      {label}
                    </div>
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: theme[0] === "dark" ? "#e5e7eb" : "#1f2937",
                      }}
                    >
                      {value}
                    </div>
                  </div>
                );

                return (
                  <>
                    {/* Mobile: Monthly Stats Section Title */}
                    {width < 790 && (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                          marginBottom: "12px",
                        }}
                      >
                        Monthly Stats
                      </div>
                    )}

                    <div style={{ 
                      display: width >= 790 ? "flex" : "grid",
                      gridTemplateColumns: width >= 790 
                        ? undefined
                        : "repeat(2, 1fr)",
                      gap: "8px",
                      width: width >= 790 ? "auto" : "100%",
                      flexWrap: width >= 790 ? "nowrap" : undefined,
                      marginTop: width >= 790 ? "0" : "0",
                    }}>
                    {/* Net PnL */}
                    <div
                      style={{
                        backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#f3f4f6",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        minWidth: "120px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "500",
                          color: theme[0] === "dark" ? "#6b7280" : "#9ca3af",
                        }}
                      >
                        {showGross ? "Gross" : "Net"} PnL
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: monthlyStats.monthlyPnL > 0
                            ? "#26a65d"
                            : monthlyStats.monthlyPnL < 0
                            ? "#f23645"
                            : "#ED9E0E",
                        }}
                      >
                        ${monthlyStats.monthlyPnL >= 0 ? "" : "-"}{Math.abs(monthlyStats.monthlyPnL).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    {/* Percentage Return */}
                    <div
                      style={{
                        backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#f3f4f6",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        minWidth: "80px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "500",
                          color: theme[0] === "dark" ? "#6b7280" : "#9ca3af",
                        }}
                      >
                        Return %
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: monthlyStats.percentageReturn > 0
                            ? "#26a65d"
                            : monthlyStats.percentageReturn < 0
                            ? "#f23645"
                            : "#ED9E0E",
                        }}
                      >
                        {monthlyStats.percentageReturn >= 0 ? "+" : ""}{monthlyStats.percentageReturn.toFixed(2)}%
                      </div>
                    </div>

                    {/* Profit Factor */}
                    <StatCard 
                      label="Profit factor" 
                      value={monthlyStats.profitFactor >= 999 ? "∞" : monthlyStats.profitFactor.toFixed(2)}
                    />

                    {/* Trade Win % */}
                    <StatCard 
                      label="Trade win %" 
                      value={`${monthlyStats.winRate.toFixed(1)}%`}
                    />

                    {/* Avg Win/Loss */}
                    <StatCard 
                      label="Avg win/loss trade" 
                      value={monthlyStats.avgWinLoss >= 999 ? "∞" : monthlyStats.avgWinLoss.toFixed(2)}
                    />

                    {/* Total Trades */}
                    <StatCard 
                      label="Total trades" 
                      value={monthlyStats.totalTrades}
                    />

                    {/* Trading Days */}
                    <StatCard 
                      label="Trading days" 
                      value={monthlyStats.uniqueTradingDays}
                    />
                  </div>
                  </>
                );
              })()}

              {/* Mobile: Calendar Trades Section Title */}
              {width < 790 && statsView && !newsView && (
                <div
                  style={{
                    marginTop: "24px",
                    paddingTop: "16px",
                    borderTop: `2px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                      marginBottom: "12px",
                    }}
                  >
                    Daily Trades
                  </div>
                </div>
              )}

              {/* View Mode Toggles - Desktop Only */}
              {!statsView && !newsView && width >= 790 && (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#ffffff",
                    padding: "4px",
                    borderRadius: "12px",
                    border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
                  }}
                >
                  <div
                    onClick={() => handleViewToggle("daily")}
                    style={{
                      ...getButtonStyles(),
                      backgroundColor: calendarView === "daily"
                        ? theme[0] === "dark"
                          ? "rgba(34, 197, 94, 0.2)"
                          : "rgba(34, 197, 94, 0.15)"
                        : "transparent",
                      border: calendarView === "daily"
                        ? `2px solid #22c55e`
                        : "2px solid transparent",
                      color: calendarView === "daily"
                        ? "#22c55e"
                        : theme[0] === "dark"
                          ? "#9ca3af"
                          : "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <CalendarTodayIcon sx={{ fontSize: "16px" }} />
                    Daily
                  </div>
                  <div
                    onClick={() => handleViewToggle("week")}
                    style={{
                      ...getButtonStyles(),
                      backgroundColor: calendarView === "week"
                        ? theme[0] === "dark"
                          ? "rgba(34, 197, 94, 0.2)"
                          : "rgba(34, 197, 94, 0.15)"
                        : "transparent",
                      border: calendarView === "week"
                        ? `2px solid #22c55e`
                        : "2px solid transparent",
                      color: calendarView === "week"
                        ? "#22c55e"
                        : theme[0] === "dark"
                          ? "#9ca3af"
                          : "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <ViewWeekIcon sx={{ fontSize: "16px" }} />
                    Week
                  </div>
                  <div
                    onClick={() => handleViewToggle("month")}
                    style={{
                      ...getButtonStyles(),
                      backgroundColor: calendarView === "month"
                        ? theme[0] === "dark"
                          ? "rgba(34, 197, 94, 0.2)"
                          : "rgba(34, 197, 94, 0.15)"
                        : "transparent",
                      border: calendarView === "month"
                        ? `2px solid #22c55e`
                        : "2px solid transparent",
                      color: calendarView === "month"
                        ? "#22c55e"
                        : theme[0] === "dark"
                          ? "#9ca3af"
                          : "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <CalendarMonthIcon sx={{ fontSize: "16px" }} />
                    Month
                  </div>
                </div>
              )}
            </div>
          </div>

          {!statsView && !newsView && (
            <SpeedDial
              // onTouchStart={() => setTouchDevice(true)}
              FabProps={{
                sx: {
                  background: theme[0] === "dark"
                    ? "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
                    : "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                  boxShadow: theme[0] === "dark"
                    ? "0 8px 24px rgba(6, 182, 212, 0.4)"
                    : "0 8px 24px rgba(14, 165, 233, 0.4)",
                  "&:hover": {
                    background: theme[0] === "dark"
                      ? "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)"
                      : "linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)",
                    boxShadow: theme[0] === "dark"
                      ? "0 12px 32px rgba(6, 182, 212, 0.5)"
                      : "0 12px 32px rgba(14, 165, 233, 0.5)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                },
              }}
              ariaLabel="Menu"
              sx={{ position: "fixed", bottom: 40, right: 50 }}
              icon={<SpeedDialIcon />}
            >
              {speed().map((action) => (
                <SpeedDialAction
                  FabProps={{
                    sx: {
                      color: theme[0] === "dark" ? "#1a1b1e" : "#FCFCFF",
                      backgroundColor:
                        theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                      boxShadow: theme[0] === "dark"
                        ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                        : "0 4px 12px rgba(0, 0, 0, 0.15)",
                      "&:hover": {
                        backgroundColor:
                          theme[0] !== "dark" ? "#2d2e33" : "#e5e7eb",
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                    },
                  }}
                  tooltipOpen={true}
                  // tooltipOpen={touchDevice ? true : false}
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.name}
                />
              ))}
            </SpeedDial>
          )}

          {/* {!auth.backtestingMode ? (
          <CustomButtom
            variant="outlined"
            label="Reflect"
            onClick={handleOpenNewReflection}
          />
        ) : (
          <div style={{ width: "90px" }}></div>
        )} */}
        </div>

        {statsView ? (
          <div
            key={`stats-view-${showGross}`}
            style={{
              padding: width >= 790 ? "0 16px 32px 16px" : "0 16px 36px 16px",
              backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
            }}
          >
            {(() => {
              const baseTrades = auth.backtestingMode
                ? auth.trades.filter((t) => t.id?.includes("backtest-"))
                : auth.trades.filter((t) => t.id?.includes("live-"));
              
              // Filter by backtest or account depending on mode
              let filteredTrades;
              if (auth.backtestingMode) {
                if (selectedBacktestId === "All") {
                  filteredTrades = baseTrades;
                } else if (selectedBacktestId === "Missed Trades") {
                  filteredTrades = baseTrades.filter(trade => trade.missedTrade);
                } else {
                  filteredTrades = baseTrades.filter(trade => trade.testId === selectedBacktestId);
                }
              } else {
                filteredTrades = getFilteredTradesByAccount(baseTrades, selectedAccountId);
              }

              const startOfMonth = moment(currentMonth).startOf("month");
              const endOfMonth = moment(currentMonth).endOf("month");
              const startDay = startOfMonth.clone().startOf("week").add(1, "day");
              const endDay = endOfMonth.clone().endOf("week").add(1, "day");

              const calendar = [];
              let currentDay = startDay.clone();

              while (currentDay.isBefore(endDay, "day")) {
                const week = [];
                for (let i = 0; i < 7; i++) {
                  week.push(currentDay.clone());
                  currentDay.add(1, "day");
                }
                calendar.push(week);
              }

              const weeks = [];
              let weekStart = startOfMonth.clone().startOf("week").add(1, "day");
              calendar.forEach(() => {
                const weekEnd = weekStart.clone().add(6, "days");
                const weekStats = calculateWeeklyStats(
                  filteredTrades,
                  weekStart,
                  weekEnd,
                  showGross
                );
                weeks.push(weekStats);
                weekStart.add(7, "days");
              });

              return (
                <>
                  {/* Desktop: Navigation buttons + Month title */}
                  {width >= 790 && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 300px",
                        gap: "16px",
                        marginTop: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <button
                          onClick={() => {
                            const newMonth = currentMonth.clone().subtract(1, "month");
                            setCurrentMonth(newMonth);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme[0] === "dark" ? "#3d3e43" : "#d1d5db";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = theme[0] === "dark" ? "#2d2e33" : "#e5e7eb";
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e5e7eb",
                            color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                            border: "2px solid transparent",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontWeight: "600",
                            fontSize: "12px",
                            transition: "all 0.2s ease",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => {
                            setCurrentMonth(moment());
                          }}
                          onMouseEnter={(e) => {
                            if (moment().isSame(currentMonth, "month")) {
                              e.currentTarget.style.transform = "scale(1.02)";
                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(55, 136, 216, 0.3)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: moment().isSame(currentMonth, "month")
                              ? theme[0] === "dark" ? "rgba(55, 136, 216, 0.2)" : "rgba(55, 136, 216, 0.15)"
                              : theme[0] === "dark" ? "#2d2e33" : "#e5e7eb",
                            color: moment().isSame(currentMonth, "month") ? "#3788d8" : theme[0] === "dark" ? "#e5e7eb" : "#374151",
                            border: "2px solid transparent",
                            borderColor: moment().isSame(currentMonth, "month") ? "#3788d8" : "transparent",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontWeight: "600",
                            fontSize: "12px",
                            transition: "all 0.2s ease",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Today
                        </button>
                        <button
                          onClick={() => {
                            const newMonth = currentMonth.clone().add(1, "month");
                            setCurrentMonth(newMonth);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme[0] === "dark" ? "#3d3e43" : "#d1d5db";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = theme[0] === "dark" ? "#2d2e33" : "#e5e7eb";
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e5e7eb",
                            color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                            border: "2px solid transparent",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontWeight: "600",
                            fontSize: "12px",
                            transition: "all 0.2s ease",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Next
                        </button>
                      </div>
                      
                      <div style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-end", minWidth: "280px" }}>
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "600",
                            color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                          }}
                        >
                          {currentMonth.format("MMMM YYYY")}
                        </div>
                        {auth.backtestingMode && (
                          <UnarchivedTestsDropDown
                            selectedTest={selectedBacktestId}
                            onTestSelect={handleBacktestChange}
                            showBacktestTrades={() => {}}
                            label="Select Backtest"
                            includeMissedTrades={true}
                            variant="compact"
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {width >= 790 ? (
                    <>
                      {/* Desktop: Header row */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 300px",
                          gap: "16px",
                          marginBottom: "4px",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 1fr)",
                            gap: "4px",
                          }}
                        >
                          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                            <div
                              key={day}
                              style={{
                                padding: "8px",
                                textAlign: "center",
                                fontWeight: "600",
                                fontSize: "13px",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                              }}
                            >
                              {day}
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            fontWeight: "600",
                            fontSize: "13px",
                            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                          }}
                        >
                          Weekly Summary
                        </div>
                      </div>

                      {/* Desktop: Week rows with summary cards */}
                      {calendar.map((week, weekIndex) => {
                        const weekStats = weeks[weekIndex];
                        return (
                          <div
                            key={weekIndex}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 300px",
                              gap: "16px",
                              marginBottom: "4px",
                            }}
                          >
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                                gap: "4px",
                              }}
                            >
                              {week.map((day, dayIndex) => {
                                const stats = calculateDailyStats(filteredTrades, day, showGross);
                                const isToday = day.isSame(moment(), "day");
                                const isCurrentMonth = day.isSame(currentMonth, "month");
                                return (
                                  <StatsCalendarDayCell
                                    key={dayIndex}
                                    dayNumber={day.format("D")}
                                    stats={stats}
                                    isToday={isToday}
                                    isCurrentMonth={isCurrentMonth}
                                  />
                                );
                              })}
                            </div>
                            
                            {/* Weekly summary card for this week */}
                            {weekStats && (
                              <div
                                style={{
                                  backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#ffffff",
                                  border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
                                  borderRadius: "8px",
                                  padding: "12px",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "8px",
                                }}
                              >
                                <div style={{ fontSize: "12px", fontWeight: "600", color: theme[0] === "dark" ? "#9ca3af" : "#6b7280" }}>
                                  {weekStats.weekLabel}
                                </div>
                                <div>
                                  <div
                                    style={{
                                      fontSize: "20px",
                                      fontWeight: "700",
                                      color: weekStats.percentReturn > 0
                                        ? "#26a65d"
                                        : weekStats.percentReturn < 0
                                        ? "#f23645"
                                        : "#ED9E0E",
                                    }}
                                  >
                                    {weekStats.percentReturn >= 0 ? "+" : ""}{weekStats.percentReturn.toFixed(2)}%
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      color: weekStats.grossPnL > 0
                                        ? "#26a65d"
                                        : weekStats.grossPnL < 0
                                        ? "#f23645"
                                        : "#ED9E0E",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {weekStats.grossPnL >= 0 ? "+" : ""}${Math.abs(weekStats.grossPnL).toFixed(2)}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "12px",
                                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                  }}
                                >
                                  <span>{weekStats.winRate.toFixed(1)}% WR</span>
                                  <span>{weekStats.tradeCount} {weekStats.tradeCount === 1 ? "trade" : "trades"}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "0" }}>
                        {calendar.flatMap((week) => week).filter(day => day.isSame(currentMonth, "month")).map((day) => {
                        const stats = calculateDailyStats(filteredTrades, day, showGross);
                        const isToday = day.isSame(moment(), "day");
                        
                        const getPnLColor = (value) => {
                          if (value > 0) return "#26a65d";
                          if (value < 0) return "#f23645";
                          return "#ED9E0E";
                        };

                        if (stats.tradeCount === 0) return null;

                        return (
                          <div
                            key={day.format("YYYY-MM-DD")}
                            style={{
                              backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#ffffff",
                              border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
                              borderLeft: isToday ? `4px solid #3788d8` : undefined,
                              borderRadius: "8px",
                              padding: "12px",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                              <div>
                                <div
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "700",
                                    color: isToday ? "#3788d8" : theme[0] === "dark" ? "#e5e7eb" : "#374151",
                                  }}
                                >
                                  {day.format("ddd, MMM D")}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                    marginTop: "2px",
                                  }}
                                >
                                  {stats.tradeCount} {stats.tradeCount === 1 ? "trade" : "trades"}
                                </div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div
                                  style={{
                                    fontSize: "20px",
                                    fontWeight: "700",
                                    color: getPnLColor(stats.percentReturn),
                                  }}
                                >
                                  {stats.percentReturn >= 0 ? "+" : ""}{stats.percentReturn.toFixed(2)}%
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: getPnLColor(stats.grossPnL),
                                    marginTop: "2px",
                                  }}
                                >
                                  {stats.grossPnL >= 0 ? "+" : ""}${Math.abs(stats.grossPnL).toFixed(0)}
                                </div>
                              </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                              <div>
                                <div style={{ fontSize: "10px", color: theme[0] === "dark" ? "#9ca3af" : "#6b7280", marginBottom: "2px" }}>
                                  Win Rate
                                </div>
                                <div style={{ fontSize: "14px", fontWeight: "600", color: theme[0] === "dark" ? "#e5e7eb" : "#1f2937" }}>
                                  {stats.winRate.toFixed(0)}%
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: "10px", color: theme[0] === "dark" ? "#9ca3af" : "#6b7280", marginBottom: "2px" }}>
                                  Wins
                                </div>
                                <div style={{ fontSize: "14px", fontWeight: "600", color: theme[0] === "dark" ? "#10b981" : "#059669" }}>
                                  {stats.wins}
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: "10px", color: theme[0] === "dark" ? "#9ca3af" : "#6b7280", marginBottom: "2px" }}>
                                  Losses
                                </div>
                                <div style={{ fontSize: "14px", fontWeight: "600", color: theme[0] === "dark" ? "#ef4444" : "#dc2626" }}>
                                  {stats.losses}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Mobile: Weekly Summary at bottom */}
                    {weeks.some(week => week.tradeCount > 0) && (
                      <div
                        style={{
                          marginTop: "24px",
                          paddingTop: "16px",
                          borderTop: `2px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
                        }}
                      >
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
                          marginBottom: "16px",
                        }}
                      >
                        Weekly Summary
                      </div>
                      
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                      {weeks.map((weekStats, weekIndex) => {
                        const getPnLColor = (value) => {
                          if (value > 0) {
                            return "#26a65d";
                          } else if (value < 0) {
                            return "#f23645";
                          } else {
                            return "#ED9E0E";
                          }
                        };

                        const formatCurrency = (value) => {
                          const absValue = Math.abs(value);
                          if (absValue >= 1000) {
                            return `${value >= 0 ? "" : "-"}$${(absValue / 1000).toFixed(2)}K`;
                          }
                          return `$${value.toFixed(0)}`;
                        };

                        return (
                          <div
                            key={weekIndex}
                            style={{
                              padding: width >= 790 ? "16px" : "12px",
                              backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#ffffff",
                              borderRadius: "12px",
                              border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
                              transition: "all 0.2s ease",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                                marginBottom: "8px",
                              }}
                            >
                              Week {weekIndex + 1}
                            </div>

                            <div
                              style={{
                                fontSize: width >= 790 ? "24px" : "20px",
                                fontWeight: "700",
                                color: getPnLColor(weekStats.percentReturn),
                                marginBottom: "8px",
                              }}
                            >
                              {weekStats.percentReturn >= 0 ? "+" : ""}
                              {weekStats.percentReturn.toFixed(2)}%
                            </div>

                            <div
                              style={{
                                fontSize: width >= 790 ? "16px" : "14px",
                                fontWeight: "600",
                                color: getPnLColor(weekStats.grossPnL),
                                marginBottom: "12px",
                              }}
                            >
                              {formatCurrency(weekStats.grossPnL)}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "12px",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                              }}
                            >
                              <span>{weekStats.winRate.toFixed(1)}% WR</span>
                              <span>{weekStats.daysTradedCount} {weekStats.daysTradedCount === 1 ? "day" : "days"}</span>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                      </div>
                    )}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        ) : newsView ? (
          <EconomicCalendar />
        ) : calendarView === "daily" ? (
          <DailyView
            forecasts={auth.backtestingMode ? auth.forecastsBacktesting : auth.forecasts}
            selectedDate={currentMonth}
            onEventClick={handleEventClick}
            onDateChange={handleMonthChange}
          />
        ) : (
          <>
            {(() => {
              const events =
                auth.defaultValue === "All"
                  ? [
                      ...forecasts,
                      ...allTradesModeChecked(),
                      ...(auth.backtestingMode ? missedTradesModeChecked() : []),
                      ...allReflectionsModeChecked,
                      ...holidayEvents,
                    ]
                  : auth.defaultValue === "All Forecasts"
                      ? auth.backtestingMode
                        ? allForecastsBacktestingModeChecked
                        : allForecastsModeChecked
                      : auth.defaultValue === "Trades"
                        ? allTradesModeChecked()
                        : auth.defaultValue === "Missed Trades"
                          ? missedTradesModeChecked()
                          : auth.defaultValue === "Reflections"
                            ? allReflectionsModeChecked
                            : [];

              if (width < 790) {
                const startOfMonth = moment(currentMonth).startOf("month");
                const endOfMonth = moment(currentMonth).endOf("month");
                
                return (
                  <CalendarListView
                    startDate={startOfMonth}
                    endDate={endOfMonth}
                    events={events}
                    onEventClick={handleEventClick}
                    onDragStart={(e, event) => e.preventDefault()}
                    currentMonth={currentMonth}
                    onMonthChange={handleMonthChange}
                  />
                );
              }

              return (
                <CalendarGrid
                  currentMonth={currentMonth}
                  events={events}
                  viewMode={calendarView}
                  onEventClick={handleEventClick}
                  onEventMove={eventMove}
                  onMonthChange={handleMonthChange}
                />
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default Calender;

// const allForecastsBacktestingModeChecked = auth?.forecastsBacktesting?.map(
//   (f) => {
//     return {
//       title: `${f.pair}  Forecast`,
//       date: moment(f.date).format("YYYY-MM-DD"),
//       color: pickColorForPair(f.pair),
//       publicId: f._id,
//     };
//   }
// );

// const allForecastsBacktestingActiveModeChecked = auth?.forecastsBacktesting
//   ?.filter((forecast) => forecast.active.includes("true"))
//   .map((f) => {
//     return {
//       title: `${f.pair}  Forecast`,
//       date: moment(f.date).format("YYYY-MM-DD"),
//       color: pickColorForPair(f.pair),
//       publicId: f._id,
//     };
//   });